import {Action} from 'redux'
import {Cmd, Loop, LoopReducer, loop} from 'redux-loop'

import {Actions} from '../actions'
import * as authActions from '../actions/auth'
import * as coreActions from '../actions/core'
import * as modalActions from '../actions/modal'

import {captureFatalException, captureIgnoredException} from '../../../commons/src/services/logging'

import {
  Color,
  ColorId,
  Country,
  CountryCode,
  FinishGroup,
  FinishGroupId,
  Material,
  MaterialConfig,
  MaterialConfigId,
  MaterialGroup,
  MaterialGroupId,
  MaterialId,
  PrintingMethod,
  PrintingMethodId,
  Provider,
} from '../types'

export type CoreState = {
  initDone: boolean
  materialConfigs: Record<MaterialConfigId, MaterialConfig>
  colors: Record<ColorId, Color>
  materials: Record<MaterialId, Material>
  materialGroups: Record<MaterialGroupId, MaterialGroup>
  finishGroups: Record<FinishGroupId, FinishGroup>
  provider: Provider | null
  countries: Record<CountryCode, Country>
  printingMethods: Record<PrintingMethodId, PrintingMethod>
}

type CoreReducer<A extends Action = Actions> = (
  state: CoreState,
  action: A
) => CoreState | Loop<CoreState>

export const initialState: CoreState = {
  initDone: false,
  materialConfigs: {},
  colors: {},
  materials: {},
  materialGroups: {},
  finishGroups: {},
  provider: null,
  countries: {},
  printingMethods: {},
}

const init: CoreReducer<coreActions.InitAction> = state => ({
  ...state,
  initDone: true,
})

const fatalError: CoreReducer<coreActions.FatalErrorAction> = (state, {payload: error}) => {
  // Ignore promise cancelled errors
  if (error && error.name === 'PromiseCancelledError') {
    return state
  }

  // Handle feathers authentication error
  if (error && error.name === 'NotAuthenticated') {
    captureIgnoredException(error)
    return loop(state, Cmd.action(authActions.signOut()))
  }

  // Handle other
  captureFatalException(error)
  return loop(state, Cmd.action(modalActions.openFatalErrorModal(error)))
}

const initialDataReceived: CoreReducer<coreActions.InitialDataReceivedAction> = (
  state,
  {payload}
) => ({
  ...state,
  ...payload,
})

const updateProvider: CoreReducer<coreActions.UpdateProviderAction> = (
  state: CoreState,
  {payload}
) => ({
  ...state,
  provider: payload,
})

const signOut: CoreReducer<authActions.SignOutAction> = (state, action) => ({
  ...state,
  provider: null,
})

export const reducer: LoopReducer<CoreState, Actions> = (state = initialState, action) => {
  switch (action.type) {
    case 'CORE.INIT':
      return init(state, action)
    case 'CORE.FATAL_ERROR':
      return fatalError(state, action)
    case 'CORE.INITIAL_DATA_RECEIVED':
      return initialDataReceived(state, action)
    case 'CORE.UPDATE_PROVIDER':
      return updateProvider(state, action)
    case 'AUTH.SIGN_OUT':
      return signOut(state, action)
    default:
      return state
  }
}

export default reducer
